import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ChevronRight,
  Database,
  Eye,
  PlusCircle,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";
import {
  warehouseMonitoringService,
  WarehouseMonitoringEvent,
  WarehouseMonitoringLatestRow,
} from "../../../services/WarehouseMonitoringService";
import { WarehouseReservationDetailsContent } from "./WarehouseReservationDetailsContent";
import { WarehouseMultiSelectFilter } from "./WarehouseMultiSelectFilter";
import { useWarehouseFilterOptions } from "../../../hooks/useWarehouseFilterOptions";

type TabId = "events" | "latest";

const EVENT_TYPES = ["read", "created", "updated", "cancelled"];

// Each event type gets its own icon and colour so a row reads at a glance.
const EVENT_TYPE_STYLES: Record<
  string,
  { Icon: typeof Eye; className: string }
> = {
  read: { Icon: Eye, className: "bg-blue-100 text-blue-800" },
  created: { Icon: PlusCircle, className: "bg-green-100 text-green-800" },
  updated: { Icon: RefreshCw, className: "bg-amber-100 text-amber-800" },
  cancelled: { Icon: XCircle, className: "bg-red-100 text-red-800" },
};

function EventTypeBadge({ eventType }: { eventType?: string }) {
  if (!eventType) return <span className="text-gray-400">—</span>;

  const style = EVENT_TYPE_STYLES[eventType.toLowerCase()];
  const Icon = style?.Icon ?? AlertCircle;

  return (
    <span
      title={eventType}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
        style?.className ?? "bg-gray-100 text-gray-700"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {eventType}
    </span>
  );
}

const filterControlClass =
  "form-input !mt-0 !h-10 min-h-0 max-h-10 box-border py-1.5 text-sm leading-snug";

function FilterFieldSpacer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <label
        className="invisible mb-1 block select-none text-xs font-medium text-gray-500"
        aria-hidden="true"
      >
        &nbsp;
      </label>
      {children}
    </div>
  );
}

interface SelectedReservation {
  key: string;
  hotelCode: string;
  reservationId: string;
  pms: string;
  source: "latest" | "events";
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  ingestedAt?: string;
  lastSeenAt?: string;
  eventType?: string;
  sourceEndpoint?: string;
  requestId?: string | null;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US");
}

function formatStayDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US");
}

function computeNights(
  startAt: string | null | undefined,
  endAt: string | null | undefined,
): number | null {
  if (!startAt || !endAt) {
    return null;
  }

  const start = new Date(startAt).getTime();
  const end = new Date(endAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null;
  }

  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
}

function formatStatus(status: string | null | undefined): string {
  if (!status) return "—";
  return status.replace(/_/g, " ");
}

interface WarehouseMonitoringProps {
  onOpenCrmProfile?: (email: string) => void;
}

export function WarehouseMonitoring({ onOpenCrmProfile }: WarehouseMonitoringProps) {
  const [activeTab, setActiveTab] = useState<TabId>("events");
  const {
    stats,
    hotelOptions,
    pmsOptions,
    selectedHotelCodes,
    selectedPms,
    setSelectedHotelCodes,
    setSelectedPms,
    hotelCodeFilter,
    pmsFilter,
    reloadFilterOptions,
    ensureHotelNames,
    getHotelDisplayName,
  } = useWarehouseFilterOptions();
  const [events, setEvents] = useState<WarehouseMonitoringEvent[]>([]);
  const [latestRows, setLatestRows] = useState<WarehouseMonitoringLatestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedReservation, setSelectedReservation] = useState<SelectedReservation | null>(null);
  const [payloadSectionOpen, setPayloadSectionOpen] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState<unknown | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);

  const [filters, setFilters] = useState({
    reservationId: "",
    eventType: "",
    status: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
  });

  const loadEvents = useCallback(async (page = pagination.page) => {
    setTableLoading(true);
    setError("");

    try {
      const response = await warehouseMonitoringService.getEvents({
        hotelCode: hotelCodeFilter,
        pms: pmsFilter,
        reservationId: filters.reservationId || undefined,
        eventType: filters.eventType || undefined,
        page,
        pageSize: pagination.pageSize,
      });

      setEvents(response.data);
      setPagination((prev) => ({
        ...prev,
        page: response.meta.page,
        total: response.meta.total,
      }));
      await ensureHotelNames(response.data.map((row) => row.hotelCode));
    } catch (err) {
      console.error("Error loading warehouse events:", err);
      setError(err instanceof Error ? err.message : "Failed to load events");
    } finally {
      setTableLoading(false);
    }
  }, [ensureHotelNames, filters, hotelCodeFilter, pagination.page, pagination.pageSize, pmsFilter]);

  const loadLatest = useCallback(async (page = pagination.page) => {
    setTableLoading(true);
    setError("");

    try {
      const response = await warehouseMonitoringService.getLatest({
        hotelCode: hotelCodeFilter,
        pms: pmsFilter,
        reservationId: filters.reservationId || undefined,
        status: filters.status || undefined,
        page,
        pageSize: pagination.pageSize,
      });

      setLatestRows(response.data);
      setPagination((prev) => ({
        ...prev,
        page: response.meta.page,
        total: response.meta.total,
      }));
      await ensureHotelNames(response.data.map((row) => row.hotelCode));
    } catch (err) {
      console.error("Error loading warehouse snapshots:", err);
      setError(err instanceof Error ? err.message : "Failed to load snapshots");
    } finally {
      setTableLoading(false);
    }
  }, [ensureHotelNames, filters, hotelCodeFilter, pagination.page, pagination.pageSize, pmsFilter]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      await reloadFilterOptions();
      if (activeTab === "events") {
        await loadEvents(1);
      } else {
        await loadLatest(1);
      }
    } catch (err) {
      console.error("Error loading warehouse dashboard:", err);
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadEvents, loadLatest, reloadFilterOptions]);

  useEffect(() => {
    // Les stats/options de filtre sont deja chargees par useWarehouseFilterOptions
    // au montage : ici on ne charge que les donnees du tableau.
    void loadEvents(1).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = async (tab: TabId) => {
    setActiveTab(tab);
    setSelectedReservation(null);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
    setPagination((prev) => ({ ...prev, page: 1 }));

    if (tab === "events") {
      await loadEvents(1);
    } else {
      await loadLatest(1);
    }
  };

  const handleSearch = async () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (activeTab === "events") {
      await loadEvents(1);
    } else {
      await loadLatest(1);
    }
  };

  const handlePageChange = async (nextPage: number) => {
    setPagination((prev) => ({ ...prev, page: nextPage }));
    if (activeTab === "events") {
      await loadEvents(nextPage);
    } else {
      await loadLatest(nextPage);
    }
  };

  const closeDetailPanel = () => {
    setSelectedReservation(null);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
  };

  const loadReservationPayload = useCallback(async (reservation: SelectedReservation) => {
    setPayloadLoading(true);

    try {
      const payload = await warehouseMonitoringService.getPayload(
        reservation.hotelCode,
        reservation.reservationId,
        reservation.pms,
        reservation.source,
      );
      setExpandedPayload(payload);
    } catch (err) {
      console.error("Error loading payload:", err);
      setExpandedPayload({ error: "Failed to load reservation payload" });
    } finally {
      setPayloadLoading(false);
    }
  }, []);

  const openDetailPanel = (reservation: SelectedReservation) => {
    if (selectedReservation?.key === reservation.key) {
      closeDetailPanel();
      return;
    }

    setSelectedReservation(reservation);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
    void loadReservationPayload(reservation);
  };

  const togglePayloadSection = () => {
    setPayloadSectionOpen((prev) => !prev);
  };

  const renderHotelName = (hotelCode: string) => (
    <span title={hotelCode}>{getHotelDisplayName(hotelCode)}</span>
  );

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));

  if (loading && !stats) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading warehouse dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <Database className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Reservation Warehouse
            </h2>
          </div>
          <button
            onClick={refreshAll}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <div className="text-sm text-gray-500">Latest snapshots</div>
              <div className="text-2xl font-semibold text-gray-900">
                {stats.latestCount.toLocaleString("en-US")}
              </div>
            </div>
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <div className="text-sm text-gray-500">Total events</div>
              <div className="text-2xl font-semibold text-gray-900">
                {stats.eventsCount.toLocaleString("en-US")}
              </div>
            </div>
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <div className="text-sm text-gray-500">Events last 24h</div>
              <div className="text-2xl font-semibold text-indigo-600">
                {stats.eventsLast24h.toLocaleString("en-US")}
              </div>
            </div>
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <div className="text-sm text-gray-500">Hotels with data</div>
              <div className="text-2xl font-semibold text-gray-900">
                {stats.hotelsWithData.toLocaleString("en-US")}
              </div>
            </div>
          </div>
        )}

        {stats && (stats.pmsBreakdown.length > 0 || stats.eventTypeBreakdown.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Snapshots by PMS
              </h3>
              <div className="space-y-2">
                {stats.pmsBreakdown.map((item) => (
                  <div
                    key={item.pms}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="font-medium text-gray-700">{item.pms}</span>
                    <span className="text-gray-900">{item.count.toLocaleString("en-US")}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 border border-gray-200 rounded-md">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Events last 24h by type
              </h3>
              <div className="space-y-2">
                {stats.eventTypeBreakdown.length === 0 ? (
                  <div className="text-sm text-gray-500">No events in the last 24 hours</div>
                ) : (
                  stats.eventTypeBreakdown.map((item) => (
                    <div
                      key={item.eventType}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="font-medium text-gray-700">{item.eventType}</span>
                      <span className="text-gray-900">{item.count.toLocaleString("en-US")}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange("events")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "events"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Recent Events
            </button>
            <button
              onClick={() => handleTabChange("latest")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "latest"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Latest Snapshots
            </button>
          </nav>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6 xl:items-end">
          <WarehouseMultiSelectFilter
            label="Hotel"
            placeholder="All hotels"
            options={hotelOptions}
            selectedValues={selectedHotelCodes}
            onChange={setSelectedHotelCodes}
          />
          <WarehouseMultiSelectFilter
            label="PMS"
            placeholder="All PMS"
            options={pmsOptions}
            selectedValues={selectedPms}
            onChange={setSelectedPms}
          />
          <FilterFieldSpacer>
            <input
              type="text"
              value={filters.reservationId}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, reservationId: e.target.value }))
              }
              placeholder="Reservation ID"
              className={filterControlClass}
            />
          </FilterFieldSpacer>
          <FilterFieldSpacer>
            {activeTab === "events" ? (
              <select
                value={filters.eventType}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, eventType: e.target.value }))
                }
                className={filterControlClass}
              >
                <option value="">All event types</option>
                {EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                placeholder="Status"
                className={filterControlClass}
              />
            )}
          </FilterFieldSpacer>
          <FilterFieldSpacer>
            <button
              type="button"
              onClick={handleSearch}
              className="flex h-10 min-h-10 max-h-10 w-full shrink-0 items-center justify-center rounded-md bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Search className="mr-2 h-4 w-4" />
              Search
            </button>
          </FilterFieldSpacer>
        </div>

        {tableLoading ? (
          <div className="text-center py-8 text-gray-500">Loading data...</div>
        ) : activeTab === "events" ? (
          <div className="w-full [&_.table-header]:px-3 [&_.table-cell]:px-3 [&_.table-cell]:whitespace-normal [&_.table-cell]:break-words">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Ingested</th>
                  <th className="table-header">Hotel</th>
                  <th className="table-header">PMS</th>
                  <th className="table-header">Source</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Start</th>
                  <th className="table-header">End</th>
                  <th className="table-header">Nights</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="table-cell text-center text-gray-500 py-8">
                      No warehouse events found.
                    </td>
                  </tr>
                ) : (
                  events.map((event) => {
                    const rowKey = `event-${event.id}`;
                    const isSelected = selectedReservation?.key === rowKey;

                    return (
                      <tr
                        key={rowKey}
                        className={`table-row cursor-pointer ${isSelected ? "bg-indigo-50" : ""}`}
                        onClick={() =>
                          openDetailPanel({
                            key: rowKey,
                            hotelCode: event.hotelCode,
                            reservationId: event.reservationId,
                            pms: event.pms,
                            source: "events",
                            status: event.status,
                            startAt: event.startAt,
                            endAt: event.endAt,
                            ingestedAt: event.ingestedAt,
                            eventType: event.eventType,
                            sourceEndpoint: event.sourceEndpoint,
                            requestId: event.requestId,
                          })
                        }
                      >
                        <td className="table-cell text-sm">
                          {formatDate(event.ingestedAt)}
                        </td>
                        <td className="table-cell">{renderHotelName(event.hotelCode)}</td>
                        <td className="table-cell">{event.pms}</td>
                        <td className="table-cell">{event.sourceEndpoint}</td>
                        <td className="table-cell">{formatStatus(event.status)}</td>
                        <td className="table-cell text-sm">
                          {formatStayDate(event.startAt)}
                        </td>
                        <td className="table-cell text-sm">
                          {formatStayDate(event.endAt)}
                        </td>
                        <td className="table-cell text-sm">
                          {computeNights(event.startAt, event.endAt) ?? "—"}
                        </td>
                        <td className="table-cell">
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="w-full [&_.table-header]:px-3 [&_.table-cell]:px-3 [&_.table-cell]:whitespace-normal [&_.table-cell]:break-words">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Last seen</th>
                  <th className="table-header">Hotel</th>
                  <th className="table-header">PMS</th>
                  <th className="table-header">Reservation</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Source</th>
                  <th className="table-header">Start</th>
                  <th className="table-header">End</th>
                  <th className="table-header">Nights</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {latestRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="table-cell text-center text-gray-500 py-8">
                      No warehouse snapshots found.
                    </td>
                  </tr>
                ) : (
                  latestRows.map((row) => {
                    const rowKey = `latest-${row.hotelCode}-${row.pms}-${row.reservationId}`;
                    const isSelected = selectedReservation?.key === rowKey;

                    return (
                      <tr
                        key={rowKey}
                        className={`table-row cursor-pointer ${isSelected ? "bg-indigo-50" : ""}`}
                        onClick={() =>
                          openDetailPanel({
                            key: rowKey,
                            hotelCode: row.hotelCode,
                            reservationId: row.reservationId,
                            pms: row.pms,
                            source: "latest",
                            status: row.status,
                            startAt: row.startAt,
                            endAt: row.endAt,
                            lastSeenAt: row.lastSeenAt,
                            sourceEndpoint: row.lastSourceEndpoint,
                            ingestedAt: row.ingestedAt,
                          })
                        }
                      >
                        <td className="table-cell text-sm">
                          {formatDate(row.lastSeenAt)}
                        </td>
                        <td className="table-cell">{renderHotelName(row.hotelCode)}</td>
                        <td className="table-cell">{row.pms}</td>
                        <td className="table-cell font-mono text-xs">
                          {row.reservationId}
                        </td>
                        <td className="table-cell">{formatStatus(row.status)}</td>
                        <td className="table-cell">{row.lastSourceEndpoint}</td>
                        <td className="table-cell text-sm">
                          {formatStayDate(row.startAt)}
                        </td>
                        <td className="table-cell text-sm">
                          {formatStayDate(row.endAt)}
                        </td>
                        <td className="table-cell text-sm">
                          {computeNights(row.startAt, row.endAt) ?? "—"}
                        </td>
                        <td className="table-cell">
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {pagination.total > pagination.pageSize && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {pagination.page} of {totalPages} ({pagination.total.toLocaleString("en-US")} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {selectedReservation && (
          <div className="fixed inset-0 z-50 flex">
            <button
              type="button"
              aria-label="Close detail panel"
              className="absolute inset-0 bg-gray-900/40"
              onClick={closeDetailPanel}
            />
            <aside className="ml-auto w-full max-w-xl bg-white shadow-xl overflow-y-auto h-full relative z-10 flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 font-mono truncate">
                    {selectedReservation.reservationId}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">
                    {renderHotelName(selectedReservation.hotelCode)} · {selectedReservation.pms}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDetailPanel}
                  className="p-1 text-gray-500 hover:text-gray-700 shrink-0"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 border-b border-gray-200 bg-gray-50 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-400">Status</div>
                  <div className="font-medium text-gray-800">
                    {formatStatus(selectedReservation.status)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Source</div>
                  <div className="font-medium text-gray-800">
                    {selectedReservation.sourceEndpoint || "—"}
                  </div>
                </div>
                {selectedReservation.eventType && (
                  <div>
                    <div className="text-gray-400">Event type</div>
                    <div className="mt-0.5">
                      <EventTypeBadge eventType={selectedReservation.eventType} />
                    </div>
                  </div>
                )}
                {selectedReservation.requestId && (
                  <div>
                    <div className="text-gray-400">Request ID</div>
                    <div className="font-medium text-gray-800 truncate">
                      {selectedReservation.requestId}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-gray-400">Arrival</div>
                  <div className="font-medium text-gray-800">
                    {formatDate(selectedReservation.startAt)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Departure</div>
                  <div className="font-medium text-gray-800">
                    {formatDate(selectedReservation.endAt)}
                  </div>
                </div>
                {selectedReservation.ingestedAt && (
                  <div>
                    <div className="text-gray-400">
                      {selectedReservation.source === "events" ? "Ingested" : "Ingested at"}
                    </div>
                    <div className="font-medium text-gray-800">
                      {formatDate(selectedReservation.ingestedAt)}
                    </div>
                  </div>
                )}
                {selectedReservation.lastSeenAt && (
                  <div>
                    <div className="text-gray-400">Last seen</div>
                    <div className="font-medium text-gray-800">
                      {formatDate(selectedReservation.lastSeenAt)}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                <WarehouseReservationDetailsContent
                  payload={expandedPayload}
                  loading={payloadLoading && expandedPayload === null}
                  locale="en"
                  payloadSectionOpen={payloadSectionOpen}
                  onTogglePayloadSection={togglePayloadSection}
                  onOpenCrmProfile={onOpenCrmProfile}
                />
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
